import { getCustomEnvVars } from '../config/environment';

export interface EnvironmentValidationResult {
  valid: boolean;
  missing: string[];
  warnings: string[];
  environment: string;
}

export interface EnvironmentInfo {
  nodeEnv: string | undefined;
  reactAppEnv: string | undefined;
  hostname: string;
  isLocalhost: boolean;
  detectedEnvironment: string;
}

/**
 * 환경변수 검증 및 환경 정보 제공 유틸리티
 */
export class EnvironmentValidator {
  
  /**
   * 필수 환경변수 목록
   */
  private static readonly REQUIRED_VARIABLES = [
    'REACT_APP_API_GATEWAY_URL',
    'REACT_APP_ENVIRONMENT',
    'REACT_APP_VAPID_PUBLIC_KEY'
  ];

  /**
   * 권장 환경변수 목록
   */
  private static readonly RECOMMENDED_VARIABLES = [
    'REACT_APP_INVENTORY_API_URL',
    'REACT_APP_ORDER_API_URL',
    'REACT_APP_IDENTITY_API_URL',
    'REACT_APP_NOTIFICATION_API_URL'
  ];

  /**
   * 환경변수 값 가져오기 (customEnvVars 우선, 그 다음 process.env)
   */
  private static getEnvironmentVariable(varName: string): string | undefined {
    try {
      const customEnvVars = getCustomEnvVars();
      if (customEnvVars[varName]) {
        return customEnvVars[varName];
      }
    } catch (error) {
      // customEnvVars 접근 실패 시 무시하고 process.env 사용
    }
    
    return process.env[varName];
  }

  /**
   * 모든 환경변수 검증
   */
  static validateEnvironmentVariables(): EnvironmentValidationResult {
    const missing: string[] = [];
    const warnings: string[] = [];

    // 필수 환경변수 검증
    this.REQUIRED_VARIABLES.forEach(varName => {
      const value = this.getEnvironmentVariable(varName);
      if (!value || value.trim() === '') {
        missing.push(varName);
      }
    });

    // 권장 환경변수 검증
    this.RECOMMENDED_VARIABLES.forEach(varName => {
      const value = this.getEnvironmentVariable(varName);
      if (!value || value.trim() === '') {
        warnings.push(`권장 환경변수 누락: ${varName}`);
      }
    });

    // NODE_ENV 검증
    const nodeEnv = process.env.NODE_ENV;
    if (!nodeEnv) {
      warnings.push('NODE_ENV가 설정되지 않았습니다.');
    } else if (!['development', 'production', 'test'].includes(nodeEnv)) {
      warnings.push(`알 수 없는 NODE_ENV 값: ${nodeEnv}`);
    }

    // 환경 일치성 검증
    const reactAppEnv = this.getEnvironmentVariable('REACT_APP_ENVIRONMENT');
    if (nodeEnv && reactAppEnv && nodeEnv !== reactAppEnv) {
      warnings.push(`NODE_ENV(${nodeEnv})와 REACT_APP_ENVIRONMENT(${reactAppEnv})가 일치하지 않습니다.`);
    }

    const environment = this.detectEnvironment();
    const result: EnvironmentValidationResult = {
      valid: missing.length === 0,
      missing,
      warnings,
      environment
    };

    // 검증 결과 로깅
    if (result.valid) {
      console.log('[EnvironmentValidator] 환경변수 검증 통과');
      if (warnings.length > 0) {
        console.warn('[EnvironmentValidator] 경고 사항:', warnings);
      }
    } else {
      console.error('[EnvironmentValidator] 필수 환경변수 누락:', missing);
      if (warnings.length > 0) {
        console.warn('[EnvironmentValidator] 추가 경고:', warnings);
      }
    }

    return result;
  }

  /**
   * 현재 환경 정보 가져오기
   */
  static getEnvironmentInfo(): EnvironmentInfo {
    const hostname = typeof window !== 'undefined' ? window.location.hostname : 'unknown';
    const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';

    return {
      nodeEnv: process.env.NODE_ENV,
      reactAppEnv: process.env.REACT_APP_ENVIRONMENT,
      hostname,
      isLocalhost,
      detectedEnvironment: this.detectEnvironment()
    };
  }

  /**
   * 환경 감지
   */
  private static detectEnvironment(): string {
    // 1. NODE_ENV 우선 확인
    if (process.env.NODE_ENV === 'production') {
      return 'production';
    }
    if (process.env.NODE_ENV === 'development') {
      return 'development';
    }

    // 2. REACT_APP_ENVIRONMENT 확인
    if (process.env.REACT_APP_ENVIRONMENT) {
      const env = process.env.REACT_APP_ENVIRONMENT.toLowerCase();
      if (['development', 'staging', 'production'].includes(env)) {
        return env;
      }
    }

    // 3. 호스트명 기반 판단
    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname;
      if (hostname.includes('staging')) {
        return 'staging';
      }
      if (hostname === 'localhost' || hostname === '127.0.0.1') {
        return 'development';
      }
    }

    // 4. 기본값
    return 'production';
  }

  /**
   * 특정 환경변수 값 안전하게 가져오기
   */
  static getEnvVariable(key: string, defaultValue?: string): string | undefined {
    const value = this.getEnvironmentVariable(key);
    
    if (value !== undefined && value.trim() !== '') {
      return value;
    }

    if (defaultValue !== undefined) {
      console.warn(`[EnvironmentValidator] ${key} 환경변수가 없어 기본값 사용: ${defaultValue}`);
      return defaultValue;
    }

    return undefined;
  }

  /**
   * 환경변수 값 검증
   */
  static validateEnvValue(key: string, value: string | undefined, validator: (val: string) => boolean): boolean {
    if (!value) {
      console.error(`[EnvironmentValidator] ${key} 환경변수가 설정되지 않았습니다.`);
      return false;
    }

    if (!validator(value)) {
      console.error(`[EnvironmentValidator] ${key} 환경변수 값이 유효하지 않습니다: ${value}`);
      return false;
    }

    return true;
  }

  /**
   * URL 형식 검증기
   */
  static isValidUrl(url: string): boolean {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * 환경별 설정 권장 사항 확인
   */
  static getRecommendations(): string[] {
    const recommendations: string[] = [];
    const envInfo = this.getEnvironmentInfo();

    // 프로덕션 환경 권장 사항
    if (envInfo.detectedEnvironment === 'production') {
      const gatewayUrl = process.env.REACT_APP_API_GATEWAY_URL;
      if (gatewayUrl && gatewayUrl.startsWith('http://')) {
        recommendations.push('프로덕션 환경에서는 HTTPS 사용을 권장합니다.');
      }

      if (!process.env.REACT_APP_VAPID_PUBLIC_KEY) {
        recommendations.push('푸시 알림을 위해 REACT_APP_VAPID_PUBLIC_KEY 설정을 권장합니다.');
      }
    }

    // 개발 환경 권장 사항
    if (envInfo.detectedEnvironment === 'development') {
      if (!envInfo.isLocalhost) {
        recommendations.push('개발 환경에서는 localhost 사용을 권장합니다.');
      }
    }

    return recommendations;
  }

  /**
   * 환경변수 상태 리포트 생성
   */
  static generateReport(): {
    validation: EnvironmentValidationResult;
    environmentInfo: EnvironmentInfo;
    recommendations: string[];
    allVariables: Record<string, string | undefined>;
  } {
    const validation = this.validateEnvironmentVariables();
    const environmentInfo = this.getEnvironmentInfo();
    const recommendations = this.getRecommendations();

    // React 앱 환경변수만 필터링 (보안상 이유)
    const allVariables: Record<string, string | undefined> = {};
    
    // process.env에서 수집
    Object.keys(process.env).forEach(key => {
      if (key.startsWith('REACT_APP_') || key === 'NODE_ENV') {
        allVariables[key] = process.env[key];
      }
    });
    
    // customEnvVars에서도 수집 (우선순위)
    try {
      const customEnvVars = getCustomEnvVars();
      Object.keys(customEnvVars).forEach(key => {
        if (key.startsWith('REACT_APP_') || key === 'NODE_ENV') {
          allVariables[key] = customEnvVars[key];
        }
      });
    } catch (error) {
      // customEnvVars 접근 실패 시 무시
    }

    return {
      validation,
      environmentInfo,
      recommendations,
      allVariables
    };
  }
}

/**
 * 환경변수 자동 검증 (앱 시작 시 호출)
 */
export const validateEnvironmentOnStartup = (): void => {
  console.log('[EnvironmentValidator] 시작 시 환경변수 검증 실행');
  
  const result = EnvironmentValidator.validateEnvironmentVariables();
  
  if (!result.valid) {
    console.group('❌ 환경변수 검증 실패');
    console.error('누락된 필수 환경변수:', result.missing);
    console.error('이 환경변수들을 .env 파일에 설정해주세요.');
    console.groupEnd();
  }

  if (result.warnings.length > 0) {
    console.group('⚠️ 환경변수 경고');
    result.warnings.forEach(warning => console.warn(warning));
    console.groupEnd();
  }

  // 브라우저 전역 객체에 디버깅용 함수 추가
  if (typeof window !== 'undefined') {
    (window as any).envValidator = {
      validate: () => EnvironmentValidator.validateEnvironmentVariables(),
      getInfo: () => EnvironmentValidator.getEnvironmentInfo(),
      getReport: () => EnvironmentValidator.generateReport(),
      getRecommendations: () => EnvironmentValidator.getRecommendations()
    };
    
    console.log('[EnvironmentValidator] window.envValidator로 디버깅 도구 사용 가능');
  }
};

export default EnvironmentValidator;